import { useAlarmsViews } from '#instructions/virtualViews/catalog/alarms.ts';
import { useAllocatedRatesVfmViews } from '#instructions/virtualViews/catalog/allocatedRatesVfm.ts';
import { useDailyRollupViews } from '#instructions/virtualViews/catalog/dailyRollup.ts';
import { useEntityViews } from '#instructions/virtualViews/catalog/entityViews.ts';
import { useEspDetailsViews } from '#instructions/virtualViews/catalog/espDetails.ts';
import { useEspGasLiftViews } from '#instructions/virtualViews/catalog/espGasLift.ts';
import { useEstimatedRatesViews } from '#instructions/virtualViews/catalog/estimatedRates.ts';
import { useExtraRatesViews } from '#instructions/virtualViews/catalog/extraRates.ts';
import { useExtraScopedViews } from '#instructions/virtualViews/catalog/extraScoped.ts';
import { useExtraStatusViews } from '#instructions/virtualViews/catalog/extraStatus.ts';
import { useHierarchyViews } from '#instructions/virtualViews/catalog/hierarchy.ts';
import { useLiftTypeParameterViews } from '#instructions/virtualViews/catalog/liftTypeParameters.ts';
import { useNetworkInputsViews } from '#instructions/virtualViews/catalog/networkInputs.ts';
import { useNetworkOutputsFieldViews } from '#instructions/virtualViews/catalog/networkOutputsField.ts';
import { useNetworkOutputsReservoirViews } from '#instructions/virtualViews/catalog/networkOutputsReservoir.ts';
import { useNetworkOutputsStringViews } from '#instructions/virtualViews/catalog/networkOutputsString.ts';
import { useNetworkRiskViews } from '#instructions/virtualViews/catalog/networkRisk.ts';
import { usePtsOutputsViews } from '#instructions/virtualViews/catalog/ptsOutputs.ts';
import { useRecordsViews } from '#instructions/virtualViews/catalog/records.ts';
import { scenarioConstraintViews } from '#instructions/virtualViews/catalog/scenarioConstraints.ts';
import { useScenarioRateSeriesViews } from '#instructions/virtualViews/catalog/scenarioRateSeries.ts';
import { useScopedOutputsViews } from '#instructions/virtualViews/catalog/scopedOutputs.ts';
import { useStringOutputsViews } from '#instructions/virtualViews/catalog/stringOutputs.ts';
import type { VirtualView } from '#instructions/virtualViews/createReshapedView.ts';

/**
 * Single source of truth for catalog virtual-view membership AND registration
 * order. Calls every catalog producer exactly once, unconditionally, in the
 * order below. That order is a load-bearing priority contract: the provider
 * registry resolves a dataset name first-claims-wins, so entry order within a
 * group is priority order (see `useProviderRegistry`). Producers are a mix of
 * plain functions and hooks; every entry is called unconditionally in this
 * fixed order, so interleaving hooks among functions is Rules-of-Hooks-safe.
 *
 * Adding a catalog family is a single edit here; an omission is locally
 * obvious instead of silently dropping a virtual-view group.
 */
export const useCatalogViews = (): readonly VirtualView[] => [
  ...useNetworkInputsViews(),
  ...useDailyRollupViews(),
  ...useAlarmsViews(),
  ...useRecordsViews(),
  ...scenarioConstraintViews(),
  ...useScopedOutputsViews(),
  ...useNetworkOutputsFieldViews(),
  ...useNetworkOutputsReservoirViews(),
  ...useNetworkRiskViews(),
  ...useScenarioRateSeriesViews(),
  ...useEntityViews(),
  ...useHierarchyViews(),
  ...useLiftTypeParameterViews(),
  ...useStringOutputsViews(),
  ...useNetworkOutputsStringViews(),
  ...useEstimatedRatesViews(),
  ...useExtraRatesViews(),
  ...useAllocatedRatesVfmViews(),
  ...usePtsOutputsViews(),
  ...useEspGasLiftViews(),
  ...useEspDetailsViews(),
  ...useExtraStatusViews(),
  ...useExtraScopedViews(),
];
